import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './MemoryGame.css';

export type PexesoPair = {
  id: string;
  photo: string;
  cari: string;
  alt: string;
};

type Variant = 'photo' | 'cari';

type CardState = {
  key: string;
  pairId: string;
  variant: Variant;
  src: string;
  alt: string;
  pos: number;
  flipped: boolean;
  matched: boolean;
  celebrating: boolean;
};

type Phase = 'peek' | 'awaiting-swipe' | 'shuffling' | 'playing' | 'complete';

export type GameStats = {
  clicks: number;
  pairs: number;
};

type Props = {
  pairs: PexesoPair[];
  /** Fires when a pair is matched. `rects` are the viewport rects of the
      two matched cards, so Hero can launch a "letters fly out" animation
      from their midpoint to the next reveal target. */
  onPairFound: (rects: DOMRect[]) => void;
  onComplete: (stats: GameStats) => void;
  onSkip: (stats: GameStats) => void;
  /** Called exactly once, when the player performs the intro swipe that
      triggers the shuffle. Hero uses it to flip the tagline from present
      to past tense. */
  onSwiped?: () => void;
};

/* Tunable timings (ms). Jan's preference: slow intro is fine. */
const PEEK_MS = 2000;
const SHUFFLE_MS = 1200;                 // duration of the FLIP glide
const SWIPE_MIN_DISTANCE_PX = 32;        // horizontal travel before it counts as a rightward swipe
const MATCH_REVEAL_DELAY_MS = 450;
const MATCH_CELEBRATION_MS = 800;
const MISMATCH_DELAY_MS = 1000;
const ATTEMPTS_FOR_SKIP = 1;             // pair attempts (match or not) before skip link shows

function shuffleInPlace<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildInitialCards(pairs: PexesoPair[]): CardState[] {
  return pairs.flatMap((p, idx) => [
    {
      key: `${p.id}-photo`,
      pairId: p.id,
      variant: 'photo' as Variant,
      src: p.photo,
      alt: `${p.alt} — photo`,
      pos: idx * 2,
      flipped: true,
      matched: false,
      celebrating: false,
    },
    {
      key: `${p.id}-cari`,
      pairId: p.id,
      variant: 'cari' as Variant,
      src: p.cari,
      alt: `${p.alt} — illustration`,
      pos: idx * 2 + 1,
      flipped: true,
      matched: false,
      celebrating: false,
    },
  ]);
}

export function MemoryGame({ pairs, onPairFound, onComplete, onSkip, onSwiped }: Props) {
  const { t } = useTranslation();
  const [cards, setCards] = useState<CardState[]>(() => buildInitialCards(pairs));
  const [phase, setPhase] = useState<Phase>('peek');
  const [flippedKeys, setFlippedKeys] = useState<string[]>([]);
  const [attempts, setAttempts] = useState(0);

  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const addTimer = (id: ReturnType<typeof setTimeout>) => timers.current.push(id);

  /* Count every card click during the playing phase (including mismatches).
     Kept in a ref so the value read inside the async match/complete timers
     is always current, regardless of re-renders. */
  const clicksRef = useRef(0);
  // Guard so the intro runs exactly once, even under React.StrictMode
  // which double-invokes effects in development.
  const introStarted = useRef(false);

  /* Swipe gesture: while phase === 'awaiting-swipe' the board listens for
     pointer drag. During the drag the card stack translates + tilts with
     the cursor (Tinder-style feedback). Release past SWIPE_MIN_DISTANCE_PX
     commits the swipe → triggers the shuffle; a short drag snaps back. */
  const swipeStart = useRef<{ x: number; y: number } | null>(null);
  const [dragDx, setDragDx] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  /* FLIP-technique refs. Before changing card positions we stash each
     card's current bounding rect into `pendingFlip.current`; after
     React re-renders, useLayoutEffect reads the new rect, computes the
     delta and plays a Web Animation that glides the card from its old
     spot to its new one. Produces a real "shuffle" — cards visibly
     swap places instead of rotating in place. */
  const cardEls = useRef<Map<string, HTMLButtonElement>>(new Map());
  const pendingFlip = useRef<Map<string, { left: number; top: number }> | null>(null);

  const captureRects = () => {
    const map = new Map<string, { left: number; top: number }>();
    cardEls.current.forEach((el, key) => {
      const r = el.getBoundingClientRect();
      map.set(key, { left: r.left, top: r.top });
    });
    pendingFlip.current = map;
  };

  const runShuffle = () => {
    captureRects();
    setCards((prev) => {
      const positions = shuffleInPlace(prev.map((_, i) => i));
      return prev.map((c, i) => ({ ...c, pos: positions[i] }));
    });
    setPhase('shuffling');
    addTimer(setTimeout(() => setPhase('playing'), SHUFFLE_MS));
  };

  const handleSwipe = (dx: number, dy: number) => {
    /* Tinder-style: only a left→right horizontal swipe counts.
       Ignore up/down/right-to-left so the arrow is the single cue.
       Shuffle is intentionally identical every time — the swipe just
       triggers it, the animation doesn't reflect the swipe vector. */
    if (dx < SWIPE_MIN_DISTANCE_PX) return;
    if (Math.abs(dy) > Math.abs(dx)) return;
    onSwiped?.();
    runShuffle();
  };

  /* Window-level pointer listener during awaiting-swipe — robust against
     disabled buttons / child event handlers swallowing the gesture.
     pointermove provides live drag feedback; pointerup commits or snaps
     back. A single effect installs listeners only while we're in this
     phase, tears them down otherwise. */
  useEffect(() => {
    if (phase !== 'awaiting-swipe') return;

    const onDown = (e: PointerEvent) => {
      swipeStart.current = { x: e.clientX, y: e.clientY };
      setIsDragging(true);
    };
    const onMove = (e: PointerEvent) => {
      if (!swipeStart.current) return;
      const dx = e.clientX - swipeStart.current.x;
      // Only show rightward drag feedback (ignore left/pull-back).
      setDragDx(Math.max(0, dx));
    };
    const onUp = (e: PointerEvent) => {
      if (!swipeStart.current) {
        setIsDragging(false);
        setDragDx(0);
        return;
      }
      const dx = e.clientX - swipeStart.current.x;
      const dy = e.clientY - swipeStart.current.y;
      swipeStart.current = null;
      setIsDragging(false);
      setDragDx(0); // CSS transition snaps back if we don't commit
      handleSwipe(dx, dy);
    };
    const onCancel = () => {
      swipeStart.current = null;
      setIsDragging(false);
      setDragDx(0);
    };

    window.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    return () => {
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
    };
  }, [phase]);

  /* Intro choreography: peek → flip-down → WAIT for swipe → shuffle → play */
  useEffect(() => {
    if (introStarted.current) return;
    introStarted.current = true;

    addTimer(setTimeout(() => {
      setCards((prev) => prev.map((c) => ({ ...c, flipped: false })));
      setPhase('awaiting-swipe');
    }, PEEK_MS));

    return () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };
  }, []);

  /* After every render, if we just changed positions, animate the glide
     using CSS transitions + inline styles. (Web Animations API would be
     cleaner, but some headless browsers stall rAF and leave animations
     pinned at t=0, which then blocks the :hover transform.) */
  useLayoutEffect(() => {
    if (!pendingFlip.current) return;
    const oldRects = pendingFlip.current;
    pendingFlip.current = null;

    cardEls.current.forEach((el, key) => {
      const old = oldRects.get(key);
      if (!old) return;
      const curr = el.getBoundingClientRect();
      const dx = old.left - curr.left;
      const dy = old.top - curr.top;
      if (dx === 0 && dy === 0) return;

      // 1) jump to old position instantly (no transition)
      el.style.transition = 'none';
      el.style.transform = `translate(${dx}px, ${dy}px)`;
      // 2) force a reflow so the browser commits the jump before we
      //    install the transition
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      el.offsetHeight;
      // 3) install the glide transition and clear the transform in the
      //    next frame so the browser sees the change as animated
      requestAnimationFrame(() => {
        el.style.transition = `transform ${SHUFFLE_MS}ms cubic-bezier(0.55, 0.05, 0.25, 1)`;
        el.style.transform = '';
      });
      // 4) after the glide, strip inline styles so CSS (including
      //    :hover) takes over again. Disable transitions during the
      //    reset so clearing `transform` doesn't fire a second glide.
      window.setTimeout(() => {
        el.style.transition = 'none';
        el.style.transform = '';
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        el.offsetHeight;
        requestAnimationFrame(() => {
          el.style.transition = '';
        });
      }, SHUFFLE_MS + 80);
    });
  });

  const skip = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setCards((prev) => prev.map((c) => ({ ...c, flipped: true, matched: true })));
    setPhase('complete');
    onSkip({ clicks: clicksRef.current, pairs: pairs.length });
  }, [onSkip, pairs.length]);

  const handleCardClick = (key: string) => {
    if (phase !== 'playing') return;
    if (flippedKeys.length >= 2) return;
    const card = cards.find((c) => c.key === key);
    if (!card || card.matched || card.flipped) return;

    clicksRef.current += 1;
    const newFlipped = [...flippedKeys, key];
    setFlippedKeys(newFlipped);
    setCards((prev) => prev.map((c) => (c.key === key ? { ...c, flipped: true } : c)));

    if (newFlipped.length < 2) return;

    const first = cards.find((c) => c.key === newFlipped[0]);
    const second = card;
    const isMatch = !!first && first.pairId === second.pairId;

    if (isMatch) {
      addTimer(setTimeout(() => {
        setCards((prev) =>
          prev.map((c) =>
            newFlipped.includes(c.key)
              ? { ...c, matched: true, celebrating: true }
              : c,
          ),
        );
        setFlippedKeys([]);
        setAttempts((a) => a + 1);

        /* Grab viewport rects of the two matched cards so Hero can fly
           the reveal text out of their midpoint. Done right as they
           light up matched so the geometry is stable. */
        const rects = newFlipped
          .map((k) => cardEls.current.get(k)?.getBoundingClientRect())
          .filter((r): r is DOMRect => !!r);
        onPairFound(rects);

        addTimer(setTimeout(() => {
          setCards((prev) => {
            const next = prev.map((c) => ({ ...c, celebrating: false }));
            if (next.every((c) => c.matched)) {
              setPhase('complete');
              onComplete({ clicks: clicksRef.current, pairs: pairs.length });
            }
            return next;
          });
        }, MATCH_CELEBRATION_MS));
      }, MATCH_REVEAL_DELAY_MS));
    } else {
      addTimer(setTimeout(() => {
        setCards((prev) =>
          prev.map((c) =>
            newFlipped.includes(c.key) ? { ...c, flipped: false } : c,
          ),
        );
        setFlippedKeys([]);
        setAttempts((a) => a + 1);
      }, MISMATCH_DELAY_MS));
    }
  };

  const canSkip =
    phase === 'playing' && attempts >= ATTEMPTS_FOR_SKIP;

  const boardClass = useMemo(
    () =>
      [
        'memory-board',
        phase === 'shuffling' ? 'is-shuffling' : '',
        phase === 'peek' ? 'is-peek' : '',
        phase === 'awaiting-swipe' ? 'is-awaiting-swipe' : '',
        phase === 'complete' ? 'is-complete' : '',
      ]
        .filter(Boolean)
        .join(' '),
    [phase],
  );

  return (
    <div className="memory-game" data-phase={phase}>
      {canSkip && (
        <button
          className="memory-skip"
          onClick={skip}
          aria-label={t('hero.skipGame')}
        >
          {t('hero.skipGame')} →
        </button>
      )}

      {phase === 'awaiting-swipe' && (
        <div
          className={`memory-swipe-hint ${isDragging ? 'is-grabbing' : ''}`}
          aria-hidden
          style={{
            // Fade the hint out once the guest has grabbed on, so it
            // doesn't fight the card drag visually.
            opacity: isDragging ? Math.max(0, 1 - dragDx / 120) : undefined,
          }}
        >
          <svg viewBox="0 0 240 80" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet">
            <defs>
              <linearGradient id="swipe-arrow-g" x1="0" x2="1" y1="0" y2="0">
                <stop offset="0%" stopColor="#B60070" stopOpacity="0.15" />
                <stop offset="35%" stopColor="#B60070" stopOpacity="0.7" />
                <stop offset="100%" stopColor="#B60070" stopOpacity="1" />
              </linearGradient>
            </defs>
            <path
              d="M8 40 L200 40 M172 18 L216 40 L172 62"
              fill="none"
              stroke="url(#swipe-arrow-g)"
              strokeWidth="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="memory-swipe-hint__label">swipe</span>
        </div>
      )}

      <div
        className={boardClass}
        style={
          phase === 'awaiting-swipe'
            ? {
                transform: `translateX(${dragDx}px) rotate(${dragDx * 0.035}deg)`,
                transition: isDragging
                  ? 'none'
                  : 'transform 350ms cubic-bezier(0.4, 0, 0.2, 1)',
              }
            : undefined
        }
      >
        {cards.map((c) => (
          <button
            key={c.key}
            ref={(el) => {
              if (el) cardEls.current.set(c.key, el);
              else cardEls.current.delete(c.key);
            }}
            type="button"
            className={[
              'memory-card',
              c.flipped ? 'is-flipped' : '',
              c.matched ? 'is-matched' : '',
              c.celebrating ? 'is-celebrating' : '',
              `memory-card--${c.variant}`,
            ]
              .filter(Boolean)
              .join(' ')}
            style={{ order: c.pos }}
            onClick={() => handleCardClick(c.key)}
            /* Only matched cards are truly disabled — pointer events must
               still bubble through unmatched cards so the board-level swipe
               handler works during awaiting-swipe. handleCardClick's phase
               guard safely no-ops clicks outside the playing phase. */
            disabled={c.matched}
            aria-label={c.alt}
          >
            <div className="memory-card__inner">
              <div className="memory-card__face memory-card__face--back">
                <img
                  src="/assets/card-back.webp"
                  alt=""
                  aria-hidden
                  loading="eager"
                  decoding="sync"
                />
              </div>
              <div className="memory-card__face memory-card__face--front">
                <img
                  src={c.src}
                  alt={c.alt}
                  loading="eager"
                  decoding="sync"
                  /* fetchpriority is valid HTML but not yet in React's JSX typings */
                  {...({ fetchpriority: 'high' } as Record<string, string>)}
                />
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

/* Card back lives as a static asset (public/assets/card-back.webp),
   rendered via <img> directly inside .memory-card__face--back. It's
   the magenta + chalk "n&j" artwork from Nicole's Pexeso.pdf
   (page 2, right-half object). Keeping it as a file lets Nicole
   swap the design without touching code. */
